import { useCallback } from "react";
import { NavigateOptions, To, useLocation, useNavigate } from "react-router";

export const useCustomNavigate = () => {
  const routerNavigate = useNavigate();
  const location = useLocation();

  const navigate = useCallback(
    (to: To, options?: NavigateOptions) => {
      // Only track "from" location if not replacing (replace doesn't add to history)
      const fromLocation = !options?.replace
        ? location.pathname + location.search
        : options?.state?.from;

      // Only save to sessionStorage if we have a valid fromLocation and not replacing
      // This prevents breaking history when using replace
      if (!options?.replace && fromLocation) {
        sessionStorage.setItem("booking-v4-from-location", fromLocation);
      }

      routerNavigate(to, {
        ...options,
        state: {
          from: fromLocation,
        },
      });
    },
    [location.pathname, location.search, routerNavigate],
  );

  const goBack = useCallback(() => {
    // Priority:
    // 1. Use location.state.from (from React Router state)
    // 2. Use sessionStorage (fallback for cross-tab navigation)
    // 3. Use browser history (-1) - this is the correct way to go back
    const fromLocation =
      location.state?.from ??
      sessionStorage.getItem("booking-v4-from-location");

    if (fromLocation) {
      // Navigate to the stored location
      navigate(fromLocation, { replace: false });
    } else {
      // Use browser's native back functionality
      navigate(-1 as To);
    }
  }, [location.state?.from, navigate]);

  return { goBack, navigate };
};
